import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../../../types/store';
import { getModuleConfig, getModuleData, getModuleMeta } from '../../../selectors/modules';
import { fetchModuleConfig, saveModuleConfig } from '../../../actions/modules';
import { actions as modalActions } from '@wix/da-shared-react/pkg/Modals';
import type { Props as ConfirmationModalProps } from '@wix/da-shared-react/pkg/Popup/ConfirmationModal';

export interface UseModuleConfigResult {
  moduleConfig?: {
    config: any;
    options?: any;
  };
  fetchModuleConfig: () => void;
  saveModuleConfig: (payload: any) => void;
}

export function useModuleConfig(moduleId: number): UseModuleConfigResult {
  const moduleConfigRaw = useSelector((state: AppState) =>
    getModuleConfig(state, moduleId)
  );
  const isModuleConfigEmpty = Object.keys(moduleConfigRaw || {}).length === 0;
  const moduleConfig = isModuleConfigEmpty ? undefined : moduleConfigRaw;

  const dispatch = useDispatch();
  const fetchConfig = useCallback(
    () => dispatch(fetchModuleConfig({ moduleId })),
    [dispatch, moduleId]
  );
  const saveConfig = useCallback(
    (payload: any) => dispatch(saveModuleConfig({ ...payload, moduleId })),
    [dispatch, moduleId]
  );

  useEffect(() => {
    if (!moduleConfig) {
      fetchConfig();
    }
  }, [moduleConfig, fetchConfig]);

  return {
    moduleConfig,
    fetchModuleConfig: fetchConfig,
    saveModuleConfig: saveConfig,
  };
}

export function useModuleMeta(moduleId: number) {
  const moduleMeta = useSelector((state: AppState) =>
    getModuleMeta(state, moduleId)
  );
  return { moduleMeta };
}

export function useModuleData(moduleId: number) {
  const moduleData = useSelector((state: AppState) =>
    getModuleData(state, moduleId)
  );
  return { moduleData };
}

export function useConfirmationModal() {
  const dispatch = useDispatch();
  const pushConfirmationModal = useCallback(
    (modalProps: ConfirmationModalProps) => {
      dispatch(modalActions.pushConfirmationModal(modalProps));
    },
    [dispatch]
  );
  return { pushConfirmationModal };
}
